import { useEffect, useState } from "react";
import { CreditCard, CheckCircle2, ShieldAlert, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { Card, PageHeader, StatusBadge, InlineNotice } from "../components/ui";

interface SubscriptionData {
  status: string;
  plan: {
    code: string;
    name: string;
    description: string;
    features: Array<{
      key: string;
      enabled: boolean;
      limit: number | null;
    }>;
  };
  trialEndsAt?: string | null;
  currentPeriodStart: string;
  currentPeriodEnd?: string | null;
  usage: {
    stores: { current: number; limit: number | null };
    users: { current: number; limit: number | null };
    products?: { current: number; limit: number | null };
  };
  effectiveEntitlements?: {
    canWrite: boolean;
    canExport: boolean;
    canLogin: boolean;
    subscriptionStatus: string;
  };
}

export function SubscriptionPage() {
  const [data, setData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api<SubscriptionData>("/subscription");
        setData(res);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Không thể tải thông tin gói cước");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleCheckout(planCode: string) {
    setBusy(true);
    try {
      const res = await api<{ checkoutUrl: string }>("/subscription/checkout", {
        method: "POST",
        body: JSON.stringify({
          planCode,
          returnUrl: window.location.href,
          cancelUrl: window.location.href,
        }),
      });
      if (res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Không thể tạo phiên thanh toán");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="page-stack"><PageHeader title="Gói Dịch Vụ BLed" /><p>Đang tải thông tin gói cước...</p></div>;
  }

  const statusLabel =
    data?.status === "TRIAL"
      ? "Dùng thử (Trial)"
      : data?.status === "ACTIVE"
      ? "Đang hoạt động"
      : data?.status === "READ_ONLY"
      ? "Chỉ đọc (Read-Only)"
      : data?.status === "PAST_DUE"
      ? "Quá hạn thanh toán"
      : data?.status === "GRACE_PERIOD"
      ? "Gia hạn chờ thanh toán"
      : data?.status === "SUSPENDED"
      ? "Tạm ngưng"
      : data?.status;

  const statusTone =
    data?.status === "ACTIVE"
      ? "success"
      : data?.status === "TRIAL"
      ? "info"
      : data?.status === "READ_ONLY"
      ? "warning"
      : "danger";

  return (
    <div className="page-stack">
      <PageHeader
        title="Dịch Vụ & Gói Cước"
        description="Quản lý gói dịch vụ BLed, hạn mức cửa hàng và hóa đơn thanh toán"
      />

      {error ? <InlineNotice tone="danger" title={error} /> : null}

      {data?.effectiveEntitlements?.canWrite === false ? (
        <InlineNotice
          tone="warning"
          title="Tài khoản đang ở chế độ Chỉ đọc (Read-Only)"
        >
          Thời gian dùng thử hoặc chu kỳ dịch vụ đã kết thúc. Bạn vẫn có thể xem và xuất dữ liệu, nhưng không thể tạo thêm đơn hàng, nhập kho hoặc thay đổi dữ liệu. Vui lòng gia hạn hoặc nâng cấp gói để tiếp tục ghi dữ liệu.
        </InlineNotice>
      ) : null}

      {data ? (
        <>
          <Card className="profile-card">
            <Sparkles size={28} color="#2563eb" />
            <div>
              <strong>{data.plan.name}</strong>
              <small>{data.plan.description || "Gói phần mềm Sổ Kinh Doanh"}</small>
            </div>
            <StatusBadge tone={statusTone}>{statusLabel}</StatusBadge>
          </Card>

          <section>
            <div className="section-title">
              <div><h2>Hạn Mức Sử Dụng</h2></div>
            </div>
            <div className="grid-3">
              <Card>
                <strong>Số lượng Cửa hàng</strong>
                <p style={{ fontSize: "1.5rem", fontWeight: "bold", margin: "0.5rem 0" }}>
                  {data.usage.stores.current} / {data.usage.stores.limit ?? "Không giới hạn"}
                </p>
                <small>Điểm bán đang kích hoạt thuộc tổ chức</small>
              </Card>

              <Card>
                <strong>Số lượng Thành viên</strong>
                <p style={{ fontSize: "1.5rem", fontWeight: "bold", margin: "0.5rem 0" }}>
                  {data.usage.users.current} / {data.usage.users.limit ?? "Không giới hạn"}
                </p>
                <small>Tài khoản có quyền truy cập</small>
              </Card>

              <Card>
                <strong>Số lượng Sản phẩm (SKU)</strong>
                <p style={{ fontSize: "1.5rem", fontWeight: "bold", margin: "0.5rem 0" }}>
                  {data.usage.products?.current ?? 0} / {data.usage.products?.limit ?? "1.000"}
                </p>
                <small>Mã hàng hóa đang hoạt động</small>
              </Card>
            </div>
          </section>

          <section>
            <div className="section-title">
              <div><h2>Tính Năng Trong Gói</h2></div>
            </div>
            <Card>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <li style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <CheckCircle2 size={18} color="#16a34a" /> Bán hàng, in hóa đơn, sổ quỹ tiền mặt & chuyển khoản
                </li>
                <li style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <CheckCircle2 size={18} color="#16a34a" /> Quản lý tồn kho, nhập hàng & đối chiếu công nợ
                </li>
                <li style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <CheckCircle2 size={18} color="#16a34a" /> Đóng sổ ngày và kiểm kê đối chiếu két tiền
                </li>
                <li style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <CheckCircle2 size={18} color="#16a34a" /> Quản lý hạn sử dụng & phân bổ xuất kho FEFO
                </li>
                <li style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <CheckCircle2 size={18} color="#16a34a" /> Báo cáo doanh thu, lãi gộp và sao lưu dữ liệu
                </li>
              </ul>
            </Card>
          </section>

          <section>
            <div className="section-title">
              <div><h2>Gia Hạn & Nâng Cấp Gói</h2></div>
            </div>
            <Card>
              <p style={{ marginBottom: "1rem" }}>
                {data.status === "TRIAL" && data.trialEndsAt
                  ? `Thời gian dùng thử miễn phí đến ngày: ${new Date(data.trialEndsAt).toLocaleDateString("vi-VN")}.`
                  : `Kỳ dịch vụ hiện tại: ${new Date(data.currentPeriodStart).toLocaleDateString("vi-VN")} - ${data.currentPeriodEnd ? new Date(data.currentPeriodEnd).toLocaleDateString("vi-VN") : "Vô thời hạn"}.`}
              </p>
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                <button
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => handleCheckout("STANDARD")}
                >
                  <CreditCard size={18} /> Gia hạn gói Tiêu Chuẩn (299.000 ₫ / tháng)
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={busy}
                  onClick={() => handleCheckout("PRO")}
                >
                  <Sparkles size={18} /> Nâng cấp gói Pro Đa Chi Nhánh
                </button>
              </div>
            </Card>
          </section>
        </>
      ) : (
        <InlineNotice tone="warning" title="Chưa kích hoạt gói dịch vụ" />
      )}
    </div>
  );
}
